import React from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, Link } from "@mui/material";
import { pageTitle, lead, h2Rule, paragraph, ctaSx, darkPanel, fadeUp } from "../theme/marketingStyles";

/** /welzijnsbarometer — publieksuitleg van de anonieme organisatie-barometer
 *  (v2: Cantril-ladder + BAT-12 + eigen Carefunded-werkitems, modulair per
 *  golf; k>=10, geen individuele scores). Feitelijk en consistent met de
 *  werkelijke werking (OrgBarometer/BarometerRespond) en met
 *  /privacystandaard. WHO-5 verwijderd 2026-07-02 (CC BY-NC-SA, non-commercial).
 *  B2B-pagina: sluit af met het donkere werkgeverspaneel van de homepage. */
export default function BarometerInfo() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const bullets = (keys: string[]) => (
    <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li::marker": { color: "var(--primary-300)" } }}>
      {keys.map((k) => (
        <Typography key={k} component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t(k)}</Typography>
      ))}
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ pt: { xs: 6, sm: 9 }, pb: { xs: 7, sm: 10 } }}>
        <Box sx={{ textAlign: { xs: "center", md: "left" }, ...fadeUp(0) }}>
          <Typography variant="h1" sx={pageTitle}>{t("barometerInfo.title")}</Typography>
          <Typography sx={{ ...lead, maxWidth: 640, mx: { xs: "auto", md: 0 } }}>{t("barometerInfo.intro")}</Typography>
        </Box>

        <Typography variant="h2" sx={{ ...h2Rule, mt: 0 }}>{t("barometerInfo.howTitle")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.how1")}</Typography>
        {bullets(["barometerInfo.howB1", "barometerInfo.howB2", "barometerInfo.howB3"])}

        <Typography variant="h2" sx={h2Rule}>{t("barometerInfo.instrumentsTitle")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.instruments1")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.instruments3")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.instruments2")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("barometerInfo.anonTitle")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.anon1")}</Typography>
        {bullets(["barometerInfo.anonB1", "barometerInfo.anonB2", "barometerInfo.anonB3"])}
        <Typography sx={paragraph}>
          {t("barometerInfo.anon2")}{" "}
          <Link component={RouterLink} to="/privacystandaard">{t("barometerInfo.privacyLink")}</Link>
        </Typography>

        <Typography variant="h2" sx={h2Rule}>{t("barometerInfo.useTitle")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.use1")}</Typography>
        <Typography sx={paragraph}>{t("barometerInfo.use2")}</Typography>

        {/* Slot-CTA in het donkere werkgeverspaneel — B2B-continuïteit. */}
        <Box sx={{ ...darkPanel, mt: 7, p: { xs: 4, sm: 6 }, textAlign: "center" }}>
          <Typography sx={{ mb: 2.5, color: "#B3A998", lineHeight: 1.65, maxWidth: 560, mx: "auto" }}>{t("barometerInfo.ctaLead")}</Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/contact")} sx={{ minWidth: 240, ...ctaSx }}>
            {t("barometerInfo.cta")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
